import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/auth';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../hooks/useAnalytics';
import { SEO } from '../../components/SEO';
import { GoogleSignInButton } from '../../components/auth/GoogleSignInButton';
import { Input } from '../../components/ui/Input';
import { Button } from '../../components/ui/Button';

const getTranslation = (t: (k: string) => string, key: string, fallback: string): string => {
  const translation = t(key);
  return translation === key ? fallback : translation;
};

export function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const { trackUserLogin, trackError } = useAnalytics();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await authService.login(username, password);
      login(response.token, response.userId, response.roleId);
      trackUserLogin('email');
      // Defer navigation to the next tick so AuthContext finishes propagating
      // isAuthenticated=true to PrivateRoute before /profile mounts. Without this,
      // PrivateRoute occasionally observed isAuthenticated=false and bounced back to /login.
      setTimeout(() => navigate('/profile', { replace: true }), 0);
    } catch (err: unknown) {
      let errorMessage = getTranslation(t, 'auth.loginFailed', 'Login failed. Please try again.');

      const ax = err as { response?: { data?: Record<string, string> | string }; message?: string };
      if (ax.response?.data) {
        const data = ax.response.data;
        if (typeof data === 'string') {
          errorMessage = data;
        } else if (data && typeof data === 'object') {
          errorMessage =
            data.error ||
            data.Error ||
            data.message ||
            data.Message ||
            errorMessage;
        }
      } else if (ax.message) {
        errorMessage = ax.message;
      }

      if (errorMessage.toLowerCase().includes('invalid username or password')) {
        errorMessage = getTranslation(
          t,
          'auth.invalidCredentials',
          '❌ Invalid username or password. Please check your credentials or register a new account.',
        );
      }

      setError(errorMessage);
      trackError(new Error(`Login failed [email]: ${errorMessage}`));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleCredential = async (credential: string) => {
    try {
      setIsLoading(true);
      setError('');
      const result = await authService.googleLogin(credential);
      login(result.token, result.userId, result.roleId);
      trackUserLogin('google');
      const target = result.isNewUser ? '/profile/setup' : '/profile';
      setTimeout(() => navigate(target, { replace: true }), 0);
    } catch (err: unknown) {
      console.error('Google login callback error:', err);
      const e = err as { response?: { data?: { message?: string; error?: string } }; message?: string };
      const msg =
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        getTranslation(t, 'auth.googleLoginFailed', 'Google login failed. Please try again.');
      setError(msg);
      trackError(new Error(`Login failed [google]: ${msg}`));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Sign in | InterviewR"
        description="Sign in to InterviewR to book mock interviews, manage your profile, and access full AI interview training."
        url={typeof window !== 'undefined' ? window.location.href : 'https://interview-r.com/login'}
        noindex
      />
      <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-stitch-canvas px-4 py-16 sm:px-6 lg:px-8">
        <div
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% -20%, rgb(var(--stitch-primary) / 0.25), transparent), radial-gradient(ellipse 60% 40% at 100% 0%, rgb(var(--stitch-accent) / 0.2), transparent)',
          }}
        />

        <div className="relative z-[1] w-full max-w-md">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-stitch-xl bg-stitch-primary-soft text-stitch-primary shadow-stitch-card">
              <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'wght' 500" }}>
                lock
              </span>
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-stitch-fg sm:text-3xl">
              {getTranslation(t, 'auth.welcomeBack', 'Welcome back')}
            </h1>
            <p className="mt-2 text-sm text-stitch-muted">
              {getTranslation(t, 'auth.noAccount', "Don't have an account?")}{' '}
              <Link to="/register" className="font-semibold text-stitch-primary hover:text-stitch-primary-hover">
                {getTranslation(t, 'auth.register', 'Create account')}
              </Link>
            </p>
          </div>

          <div className="rounded-stitch-2xl border border-stitch-border bg-stitch-surface p-6 shadow-stitch-modal sm:p-8">
            {error && (
              <div
                className="mb-6 rounded-stitch-xl border border-stitch-danger/30 bg-stitch-danger-soft p-4"
                role="alert"
              >
                <div className="flex gap-3">
                  <span
                    className="material-symbols-outlined flex-shrink-0 text-stitch-danger"
                    style={{ fontVariationSettings: "'wght' 500" }}
                  >
                    error
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-stitch-danger">
                      {getTranslation(t, 'common.error', 'Login failed')}
                    </h3>
                    <p className="mt-1 text-sm text-stitch-fg-soft">{error}</p>
                    {error.toLowerCase().includes('invalid') && (
                      <div className="mt-3 border-t border-stitch-border pt-3">
                        <p className="mb-2 text-xs text-stitch-muted">
                          {getTranslation(t, 'auth.accountMayNotExist', "Don't have an account yet?")}
                        </p>
                        <Link
                          to="/register"
                          className="inline-flex items-center gap-1 text-sm font-semibold text-stitch-primary hover:text-stitch-primary-hover"
                        >
                          {getTranslation(t, 'auth.createAccount', 'Create a new account')}
                          <span className="material-symbols-outlined text-base">arrow_forward</span>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            <form className="space-y-5" onSubmit={handleLogin}>
              <Input
                id="username"
                name="username"
                label={getTranslation(t, 'auth.emailOrUsername', 'Email or Username')}
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="your.email@example.com"
              />

              <Input
                id="password"
                name="password"
                label={getTranslation(t, 'auth.password', 'Password')}
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />

              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="h-4 w-4 rounded border-stitch-border text-stitch-primary focus:ring-stitch-primary"
                  />
                  <span className="text-sm text-stitch-fg-soft">
                    {getTranslation(t, 'auth.rememberMe', 'Keep me signed in')}
                  </span>
                </label>
                <Link
                  to="/auth/forgot-password"
                  className="text-sm font-medium text-stitch-primary hover:text-stitch-primary-hover"
                >
                  {getTranslation(t, 'auth.forgotPassword', 'Forgot password?')}
                </Link>
              </div>

              <Button type="submit" variant="primary" size="lg" className="w-full" disabled={isLoading}>
                {isLoading ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {getTranslation(t, 'auth.signingIn', 'Signing in...')}
                  </span>
                ) : (
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="material-symbols-outlined text-xl">login</span>
                    {getTranslation(t, 'auth.login', 'Sign in')}
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stitch-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase tracking-wider">
                  <span className="bg-stitch-surface px-3 text-stitch-muted">
                    {getTranslation(t, 'auth.orContinueWith', 'Or continue with')}
                  </span>
                </div>
              </div>
              <div className="mt-6 flex justify-center">
                <GoogleSignInButton
                  text="signin_with"
                  onCredential={handleGoogleCredential}
                  onError={(e) =>
                    setError(
                      e.message ||
                        getTranslation(t, 'auth.googleLoginFailed', 'Google login failed. Please try again.'),
                    )
                  }
                />
              </div>
            </div>
          </div>

          <p className="mt-8 text-center text-xs text-stitch-muted">
            {getTranslation(t, 'auth.loginBrandKicker', 'InterviewR')} —{' '}
            {getTranslation(
              t,
              'auth.loginBrandBullet1',
              'AI mock interviews, expert bookings, and feedback in one place.',
            )}
          </p>
        </div>
      </div>
    </>
  );
}
